import { StarIcon } from "@heroicons/react/24/solid";
import React, { FC } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { useUnifiedReviewSummaryFor } from "utils/helpers/useUnifiedRating";

export interface StartRatingProps {
  className?: string;
  point?: number;
  reviewCount?: number;
  propertyData?: PropertyToEdit | null;
  reviewTotal?: number;
  // For cards that do not have full propertyData: the property title is
  // enough to look up the legacy reviews.
  title?: string;
  // On by default: the rating merges legacy reviews with the new platform ones.
  // Pass includeLegacy={false} anywhere you want the new-platform rating only.
  includeLegacy?: boolean;
}

const StartRating: FC<StartRatingProps> = ({
  className = "",
  propertyData,
  reviewCount,
  reviewTotal,
  title,
  includeLegacy = true,
}) => {
  // New-platform numbers, from full property data or from the card's props
  const newAverage = propertyData?.average?.average_review || reviewCount || 0;
  const newCount = propertyData?.average?.count || reviewTotal || 0;

  // No title (or includeLegacy off) means no request is made.
  const unified = useUnifiedReviewSummaryFor({
    title: includeLegacy ? propertyData?.title || title : null,
    average: Number(newAverage) || 0,
    count: newCount,
  });
  const useUnified = includeLegacy && unified.rating !== null;

  // Legacy lookup still running and nothing to show yet: render nothing
  // instead of flashing "New Listing" for a house that has legacy reviews.
  const waitingForLegacy = includeLegacy && unified.loading && !newCount;
  if (waitingForLegacy) return null;

  const rawReview = useUnified
    ? unified.rating
    : propertyData?.average?.average_review || (typeof reviewCount === 'number' ? reviewCount : null) || 0;
  const review = Number(rawReview);
  const count = useUnified ? unified.count : propertyData?.average?.count || reviewTotal || 0;
  const formattedReview = review && !isNaN(review) ? review.toFixed(1) : null;
  
  // If no reviews at all, show "New" badge or "No reviews yet"
  if (!formattedReview || count === 0) {
    return (
      <div
        className={`nc-StartRating flex items-center space-x-1 text-sm ${className}`}
        data-nc-id="StartRating"
      >
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          New Listing
        </span>
      </div>
    );
  }

  return (
    <div
      className={`nc-StartRating flex items-center space-x-1 text-sm ${className}`}
      data-nc-id="StartRating"
    >
      <div className="pb-[2px]">
        <StarIcon className="w-[18px] h-[18px] text-orange-500" />
      </div>
      <span className="font-medium">{formattedReview}</span>
      <span className="text-neutral-500 dark:text-neutral-400">
        ({count} {count === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
};

export default StartRating;